import type Phaser from 'phaser';
import { Dir, type Direction } from '../../sim/direction';
import type { Intent } from '../../sim/mower';
import type { ControlScheme } from '../settings';
import { DEFAULT_GESTURES, GestureRecognizer, type Gesture } from './gestures';

export interface InputHandlers {
  /** A steering request for the sim. */
  onIntent(intent: Intent): void;
  /**
   * Any deliberate input (used to start a level or dismiss prompts). `steering` is true when the
   * input also picks a direction (a swipe, an arrow key). Returns true when it consumed the input,
   * so that same gesture does not also steer.
   */
  onActivity(steering: boolean): boolean;
  /** Debug hook: every pointer sample and recognized gesture, in game (screen) pixels. */
  onTrace?(event: TouchTrace): void;
}

export type TouchTrace =
  | { kind: 'down' | 'move' | 'up'; id: number; x: number; y: number }
  | { kind: 'gesture'; gesture: Gesture; x: number; y: number };

const KEY_DIRECTIONS: Readonly<Record<string, Direction>> = {
  ArrowUp: Dir.N,
  KeyW: Dir.N,
  ArrowRight: Dir.E,
  KeyD: Dir.E,
  ArrowDown: Dir.S,
  KeyS: Dir.S,
  ArrowLeft: Dir.W,
  KeyA: Dir.W,
};

/**
 * Maps touch, mouse and keyboard to sim intents. Swipe scheme: swipe toward the heading you want.
 * Tap scheme: tap the left/right half to turn left/right. In both, press and hold parks a mower
 * that spins in place (the Zero-Turn). Keyboard always works: arrows/WASD set a heading, Z/X turn
 * left/right, H holds.
 */
export class InputController {
  private readonly gestures = new GestureRecognizer();
  /** Pointers that began on the play area (or on a swipe-through band). */
  private readonly steering = new Set<number>();
  /** Of those, the ones that began on a swipe-through band: they steer by swipe only. */
  private readonly swipeOnly = new Set<number>();

  constructor(
    private readonly scene: Phaser.Scene,
    private scheme: ControlScheme,
    private readonly handlers: InputHandlers,
    /**
     * Where a touch starting at this height belongs: 'ui', a band whose buttons take it and which
     * never steers (the HUD); 'swipe', a band whose buttons take taps while a swipe from there
     * still steers (the toolbar, at the thumb's end of the screen); null, the lawn.
     */
    private readonly bandAt: (y: number) => 'ui' | 'swipe' | null = () => null,
  ) {
    const input = scene.input;
    input.on('pointerdown', this.onDown);
    input.on('pointermove', this.onMove);
    input.on('pointerup', this.onUp);
    input.on('pointerupoutside', this.onUp);
    input.keyboard?.on('keydown', this.onKey);
    scene.events.on('update', this.onUpdate);
    scene.events.once('shutdown', () => this.destroy());
  }

  setScheme(scheme: ControlScheme): void {
    this.scheme = scheme;
  }

  destroy(): void {
    const input = this.scene.input;
    input.off('pointerdown', this.onDown);
    input.off('pointermove', this.onMove);
    input.off('pointerup', this.onUp);
    input.off('pointerupoutside', this.onUp);
    input.keyboard?.off('keydown', this.onKey);
    this.scene.events.off('update', this.onUpdate);
  }

  /** Holds have no pointer event of their own: look for them every frame. */
  private readonly onUpdate = (): void => {
    for (const id of this.steering) {
      const p = this.scene.input.manager.pointers.find((q) => q.id === id);
      const holdMs = this.scheme === 'swipe' ? DEFAULT_GESTURES.swipeHoldMs : undefined;
      const hold = this.gestures.poll(id, this.scene.time.now, holdMs);
      if (hold && p) this.handle(hold, p);
    }
  };

  private readonly onDown = (p: Phaser.Input.Pointer): void => {
    // Touches that begin on a UI band belong to its buttons, not to steering.
    const band = this.bandAt(p.y);
    if (band === 'ui') return;
    // One finger steers at a time: a second touch is ignored, except that a touch on the lawn
    // takes over from one resting on the toolbar (most likely an item tap, which the HUD reads).
    for (const id of this.steering) {
      const q = this.scene.input.manager.pointers.find((r) => r.id === id);
      const yields = band !== 'swipe' && this.swipeOnly.has(id);
      if (!q?.isDown || yields) {
        this.steering.delete(id);
        this.swipeOnly.delete(id);
        if (q) this.gestures.up(id, q.downX, q.downY, q.downTime);
      }
    }
    if (this.steering.size > 0) return;
    this.steering.add(p.id);
    if (band === 'swipe') this.swipeOnly.add(p.id);
    else this.swipeOnly.delete(p.id);
    this.gestures.down(p.id, p.x, p.y, p.downTime);
    this.handlers.onTrace?.({ kind: 'down', id: p.id, x: p.x, y: p.y });
  };

  private readonly onMove = (p: Phaser.Input.Pointer): void => {
    if (!p.isDown || !this.steering.has(p.id)) return;
    this.handlers.onTrace?.({ kind: 'move', id: p.id, x: p.x, y: p.y });
    this.handle(this.gestures.move(p.id, p.x, p.y, p.moveTime), p);
  };

  private readonly onUp = (p: Phaser.Input.Pointer): void => {
    if (!this.steering.delete(p.id)) return;
    // A touch the system took over (an iOS home swipe) steers nothing and starts nothing.
    if (p.wasCanceled) {
      this.gestures.up(p.id, p.downX, p.downY, p.upTime);
      return;
    }
    this.handlers.onTrace?.({ kind: 'up', id: p.id, x: p.x, y: p.y });
    const gesture = this.gestures.up(p.id, p.x, p.y, p.upTime);
    // Any release starts a level, even a long press that is neither tap nor swipe (on the lawn).
    if (gesture) this.handle(gesture, p);
    else if (!this.swipeOnly.has(p.id)) this.handlers.onActivity(false);
  };

  private handle(gesture: Gesture | null, p: Phaser.Input.Pointer): void {
    if (!gesture) return;
    // From the toolbar, only a swipe that steers counts: taps and holds there are its buttons'.
    if (this.swipeOnly.has(p.id) && (gesture.kind !== 'swipe' || this.scheme !== 'swipe')) return;
    this.handlers.onTrace?.({ kind: 'gesture', gesture, x: p.x, y: p.y });
    const steering = gesture.kind === 'swipe' && this.scheme === 'swipe';
    if (this.handlers.onActivity(steering)) return;
    if (gesture.kind === 'hold') {
      // Press and hold: a mower that spins in place parks to spin at the next tile.
      this.handlers.onIntent({ type: 'park' });
    } else if (steering) {
      this.handlers.onIntent({ type: 'face', dir: gesture.dir });
    } else if (gesture.kind === 'tap' && this.scheme === 'tap') {
      const turn = gesture.x < this.scene.scale.gameSize.width / 2 ? 'left' : 'right';
      this.handlers.onIntent({ type: 'turn', turn });
    }
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    const dir = KEY_DIRECTIONS[event.code];
    if (dir !== undefined) {
      if (!this.handlers.onActivity(true)) this.handlers.onIntent({ type: 'face', dir });
    } else if (event.code === 'KeyH') {
      if (!this.handlers.onActivity(false)) this.handlers.onIntent({ type: 'park' });
    } else if (event.code === 'KeyZ' || event.code === 'KeyX') {
      if (this.handlers.onActivity(false)) return;
      this.handlers.onIntent({ type: 'turn', turn: event.code === 'KeyZ' ? 'left' : 'right' });
    }
  };
}
